// tap_match — pair each prompt (left) with its match (right). The learner taps a
// left item to select it, then a right item to attach it. The correct mapping is
// pair→itself; the right column is shuffled for presentation. Submits once every
// pair has a match.

import { useMemo, useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ArabicText } from './primitives';
import type { ActivityRendererProps } from './types';

type Pair = {
  id: string;
  promptKey: string;
  promptImage?: string | null;
  matchKey: string;
};
type TapMatch = { instructionKey?: string | null; pairs: Pair[] };

// Fisher-Yates with a seedless shuffle — this is browser-side presentation, so
// non-determinism is fine (and desirable: a re-mount re-shuffles).
function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function TapMatch({ data, ctx, cb }: ActivityRendererProps<TapMatch>): ReactElement {
  const { t } = ctx;
  // The right column, shuffled once per mount.
  const rightItems = useMemo(
    () => shuffle(data.pairs.map((p) => ({ id: p.id, matchKey: p.matchKey }))),
    [data.pairs],
  );

  const [selectedLeft, setSelectedLeft] = useState<string | null>(null);
  // leftId → rightId attached by the learner.
  const [links, setLinks] = useState<Record<string, string>>({});

  function tapLeft(id: string): void {
    if (cb.locked) return;
    setSelectedLeft((cur) => (cur === id ? null : id));
  }

  function tapRight(rightId: string): void {
    if (cb.locked || !selectedLeft) return;
    setLinks((cur) => {
      // Remove any existing attachment to this right item, then attach.
      const next: Record<string, string> = {};
      for (const [l, r] of Object.entries(cur)) {
        if (r !== rightId && l !== selectedLeft) next[l] = r;
      }
      next[selectedLeft] = rightId;
      // Auto-submit once every pair is linked.
      if (Object.keys(next).length === data.pairs.length) {
        cb.onAnswer({
          kind: 'match',
          pairs: Object.entries(next).map(([id, matchedId]) => ({ id, matchedId })),
        } satisfies content.AttemptInput);
      }
      return next;
    });
    setSelectedLeft(null);
  }

  const rightToLeft = useMemo(() => {
    const m: Record<string, string> = {};
    for (const [l, r] of Object.entries(links)) m[r] = l;
    return m;
  }, [links]);

  return (
    <div className="mx-auto flex max-w-md flex-col">
      <p className="text-center text-lg font-semibold text-ink-700">
        {t(data.instructionKey ?? 'content.common.tap_match')}
      </p>

      <div className="mt-6 grid grid-cols-2 gap-x-5 gap-y-3">
        <div className="space-y-3">
          {data.pairs.map((p) => {
            const linked = Boolean(links[p.id]);
            const active = selectedLeft === p.id;
            return (
              <button
                key={p.id}
                type="button"
                disabled={cb.locked}
                onClick={() => tapLeft(p.id)}
                aria-pressed={active}
                className={`flex min-h-[56px] w-full items-center justify-center rounded-2xl border-2 px-4 py-3 text-lg font-bold transition-colors ${
                  active
                    ? 'border-burgundy-500 bg-burgundy-50 text-burgundy-700'
                    : linked
                      ? 'border-leaf bg-leaf/15 text-burgundy-700'
                      : 'border-cream-200 bg-white text-ink-900 hover:border-sun-300'
                }`}
              >
                <ArabicText text={t(p.promptKey)} ctx={ctx} />
              </button>
            );
          })}
        </div>

        <div className="space-y-3">
          {rightItems.map((r) => {
            const linked = Boolean(rightToLeft[r.id]);
            return (
              <button
                key={r.id}
                type="button"
                disabled={cb.locked || !selectedLeft}
                onClick={() => tapRight(r.id)}
                className={`flex min-h-[56px] w-full items-center justify-center rounded-2xl border-2 px-4 py-3 text-lg font-bold transition-colors ${
                  linked
                    ? 'border-leaf bg-leaf/15 text-burgundy-700'
                    : 'border-cream-200 bg-white text-ink-900 enabled:hover:border-sun-300 disabled:opacity-60'
                }`}
              >
                <ArabicText text={t(r.matchKey)} ctx={ctx} />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
